import {
    positionKey,
    parsePositionKey,
    edgeKey
} from '../../core/GridGeometry.js';

// Re-export coordinate helpers for use in other Flood files
export { positionKey, parsePositionKey, edgeKey };

// Rational number for precise flow rate calculations
export interface Rational {
    num: number;
    denom: number;
}

// Cell tags for flow resolution.
//
// `parent` is the upstream *crash* — where the falling water landed — and it is
// carried unchanged along a whole spread so the basin it feeds can be found again.
// `via` on a spread is the different, smaller thing: the neighbouring cell that
// tagged this one, i.e. the step the water actually took to get here. Only the
// spill hints need it, and only they should use it — a crash can be several cells
// away and on the far side of a wall from where the water finally arrived.
export type CellTag =
    | { type: 'drop'; parent: string | null; rate: Rational | null; resolved: boolean }
    | { type: 'crash'; parent: string | null; rate: Rational; resolved: boolean }
    | { type: 'spread'; parent: string | null; via?: string; resolved: boolean };

// Basin info collected from flow tree
export interface Basin {
    cells: string[];
    rate: Rational;
}

// A spill point: a cell where water tips over an edge and starts falling.
// `from` is the horizontal side the water arrived from (null = straight down).
export interface DropHint {
    row: number;
    col: number;
    from: 'W' | 'E' | null;
}

// Vortex slice: tracks flow state for a particular gate traversal path
export interface SliceState {
    ancestry: string[];       // Sequence of entry-gate positionKeys traversed to reach this slice
    flowState: Map<string, CellTag>;
}

// Snapshot of simulation state at a point in time
export interface FloodSnapshot {
    filled: Set<string>;
    waterLevel: Map<string, Rational>;
    flowState: Map<string, CellTag>;
    basins: Basin[];
}

// Vortex snapshot: stores per-slice flow states
export interface VortexFloodSnapshot {
    filled: Set<string>;
    waterLevel: Map<string, Rational>;
    slices: Map<string, SliceState>;  // sliceId -> SliceState
    basins: Basin[];
}

// Rational number operations
export function gcd(a: number, b: number): number {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b > 0) {
        const t = b;
        b = a % b;
        a = t;
    }
    return a;
}

export function reduceRational(r: Rational): Rational {
    if (r.num === 0) return { num: 0, denom: 1 };
    const g = gcd(r.num, r.denom);
    return { num: r.num / g, denom: r.denom / g };
}

export function addRationals(a: Rational, b: Rational): Rational {
    return reduceRational({
        num: a.num * b.denom + b.num * a.denom,
        denom: a.denom * b.denom
    });
}

export function subtractRationals(a: Rational, b: Rational): Rational {
    return reduceRational({
        num: a.num * b.denom - b.num * a.denom,
        denom: a.denom * b.denom
    });
}

export function multiplyRationals(a: Rational, b: Rational): Rational {
    return reduceRational({
        num: a.num * b.num,
        denom: a.denom * b.denom
    });
}

export function divideRational(r: Rational, n: number): Rational {
    return reduceRational({ num: r.num, denom: r.denom * n });
}

export function compareRationals(a: Rational, b: Rational): number {
    return a.num * b.denom - b.num * a.denom;
}

export function rationalToNumber(r: Rational): number {
    return r.num / r.denom;
}

/** @deprecated Use serializePosition instead */
export function colToLetter(col: number): string {
    return String.fromCharCode(65 + col);
}

// Format rational as string
export function rationalToString(r: Rational): string {
    if (r.denom === 1) return `${r.num}`;
    return `${r.num}/${r.denom}`;
}
